/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as DOMPurify from 'dompurify';
import _ from 'lodash';
import { DropdownContainer, DropdownRow, DropdownWrapper, EmptyState } from './styles';
import { InputPositionType, OVERLAY_MAX_HEIGHT, DropdownProps } from './types';

const root = document.getElementById('overlay-root');

const Dropdown: React.FC<DropdownProps> = (props) => {
  const {
    target,
    isOpen,
    onSelection,
    setIsOpen,
    setHighlightedIndex,
    highlightedIndex,
    scrollRef,
    highlightedSubString = '',
    options,
    keyProperty,
    showProperty,
    inputValue,
    isValueObject,
    chevronRef,
    targetHeight = 0,
    overlayPosition = 'auto',
    emptyStateLabel = 'Sin resultados',
    overAll = false,
    testId,
  } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const [parentRect, setParentRect] = useState<InputPositionType>({
    width: 0,
    top: 0,
    bottom: 0,
    x: 0,
  });

  const handleClose = useCallback(
    (event) => {
      const parent = document.getElementById(target);
      if (
        !divRef.current.contains(event.target) &&
        !chevronRef?.current?.contains(event.target) &&
        !parent.contains(event.target)
      ) {
        setIsOpen(false);
      }
    },
    [chevronRef, setIsOpen, target],
  );
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClose);
    } else {
      document.removeEventListener('mousedown', handleClose);
    }
    return () => document.removeEventListener('mousedown', handleClose);
  }, [handleClose, isOpen]);

  useEffect(() => {
    const parent = document.getElementById(target);
    const newRect: DOMRect = parent?.getBoundingClientRect();
    if (newRect) {
      setParentRect({
        width: newRect?.width,
        top: newRect?.top + window.scrollY,
        bottom: newRect?.bottom + window.scrollY,
        x: newRect?.x,
      });
    }
  }, [target, isOpen]);

  const position = useMemo(() => {
    const html = document.documentElement;
    const height = Math.max(html.clientHeight, html.scrollHeight);
    const bottomSpace = height - parentRect?.bottom;
    if (overlayPosition === 'top') return parentRect?.top - OVERLAY_MAX_HEIGHT - 6;
    if (overlayPosition === 'bottom') return parentRect?.top + targetHeight + 6;
    return bottomSpace < OVERLAY_MAX_HEIGHT
      ? parentRect?.top - OVERLAY_MAX_HEIGHT - 6
      : parentRect?.top + targetHeight + 6;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayPosition, parentRect?.bottom, parentRect?.top, isOpen]);

  const handleChange = useCallback(
    (valueChange: string) => () => {
      onSelection(valueChange);
      setIsOpen(false);
    },
    [onSelection, setIsOpen],
  );

  const handleHover = useCallback(
    (valueChange: string, index: number) => () => {
      setHighlightedIndex({
        index,
        id: valueChange,
      });
    },
    [setHighlightedIndex],
  );

  const renderRows = useCallback(() => {
    if (options?.length === 0) {
      return <EmptyState>{emptyStateLabel}</EmptyState>;
    }
    return options?.map((option, index) => {
      const regex = new RegExp(highlightedSubString, 'gi');
      const isActive = isValueObject
        ? _.get(option, keyProperty) === _.get(inputValue, keyProperty)
        : _.get(option, keyProperty) === inputValue;

      return (
        <DropdownRow
          key={_.get(option, keyProperty)}
          id={_.get(option, keyProperty)}
          isActive={isActive}
          isHighlighted={highlightedIndex.index === index}
          onMouseDown={handleChange(option)}
          onMouseEnter={handleHover(option, index)}
          data-testId={`${testId}-option-${index}`}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              isActive || !highlightedSubString
                ? `<p data-testId=${testId}-option-${index}-label>${_.get(
                    option,
                    showProperty,
                  )}</p>`
                : `<p data-testId=${testId}-option-${index}-label>${_.get(
                    option,
                    showProperty,
                  ).replace(regex, (match: string) => `<b>${match}</b>`)}</p>`,
              { USE_PROFILES: { html: true } },
            ),
          }}
        />
      );
    });
  }, [
    options,
    emptyStateLabel,
    highlightedSubString,
    isValueObject,
    keyProperty,
    inputValue,
    highlightedIndex.index,
    handleChange,
    handleHover,
    testId,
    showProperty,
  ]);
  return createPortal(
    <DropdownContainer
      ref={divRef}
      isVisible={isOpen}
      top={position}
      width={parentRect.width}
      left={parentRect.x}
      overAll={overAll}
      data-testid={`${testId}-dropdown`}
    >
      <DropdownWrapper ref={scrollRef}>{renderRows()}</DropdownWrapper>
    </DropdownContainer>,
    root,
  );
};

export default Dropdown;
